import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Modal,
  Spinner,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import CreateSupplierModal from '../components/CreateSupplierModal';
import type { DropdownSelectOption, UserOrganisation } from '../types/types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { gToastPosition, gToastSentiment } from '../util/constst';
import SupplierList from '../components/SupplierList';
import DropdownSelect from '../components/common/DropdownSelect';
import { useQuestionnaireTemplates } from '../hooks/useQuestionnaireTemplates';
import { supabase } from '../lib/supabase';

type InviteQuestionnaireMode = 'none' | 'standard' | 'custom';

export default function Suppliers() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteStep, setInviteStep] = useState<1 | 2>(1);

  const [reloadKey, setReloadKey] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(
    'Supplier was created successfully',
  );
  const [supplierEmails, setSupplierEmails] = useState('');
  const [questionnaireMode, setQuestionnaireMode] =
    useState<InviteQuestionnaireMode>('standard');
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState('');
  const [
    defaultOnboardingQuestionnaireId,
    setDefaultOnboardingQuestionnaireId,
  ] = useState<string | null>(null);
  const [defaultInviteIndustryId, setDefaultInviteIndustryId] = useState<
    string | null
  >(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [questionnaireError, setQuestionnaireError] = useState<string | null>(
    null,
  );
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSending, setInviteSending] = useState(false);

  const navigate = useNavigate();
  const {
    organisationTemplates,
    loading: loadingQuestionnaires,
    error: questionnairesError,
    getOrganisationTemplates,
  } = useQuestionnaireTemplates();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const questionnaireOptions = useMemo<DropdownSelectOption[]>(
    () =>
      organisationTemplates.map((template) => ({
        id: template.id,
        label: template.title,
      })),
    [organisationTemplates],
  );

  const handleCreated = () => {
    setReloadKey((prev) => prev + 1); // trigger reload
    // setShowCreateModal(false);
  };

  const handleFinished = () => {
    setShowCreateModal(false);
    setToastMessage('Supplier was created successfully');
    setShowToast(true);
  };

  useEffect(() => {
    if (!showInviteModal || !selectedOrganisation?.organisation?.id) {
      return;
    }

    getOrganisationTemplates(selectedOrganisation.organisation.id);

    const fetchDefaultOnboardingQuestionnaire = async () => {
      const [{ data, error }, { data: industryData, error: industryError }] =
        await Promise.all([
          supabase
            .from('questionnaire_templates')
            .select('id')
            .eq('type', 'default_onboarding')
            .limit(1),
          supabase
            .from('industry_options')
            .select('id')
            .order('ordinal', { ascending: true })
            .limit(1),
        ]);

      if (error) {
        setDefaultOnboardingQuestionnaireId(null);
      } else {
        setDefaultOnboardingQuestionnaireId(data?.[0]?.id ?? null);
      }

      if (industryError) {
        setDefaultInviteIndustryId(null);
      } else {
        setDefaultInviteIndustryId(industryData?.[0]?.id ?? null);
      }
    };

    fetchDefaultOnboardingQuestionnaire();
  }, [
    getOrganisationTemplates,
    selectedOrganisation?.organisation?.id,
    showInviteModal,
  ]);

  const resetInviteModal = () => {
    setShowInviteModal(false);
    setInviteStep(1);
    setSupplierEmails('');
    setQuestionnaireMode('standard');
    setSelectedQuestionnaireId('');
    setEmailError(null);
    setQuestionnaireError(null);
    setInviteError(null);
    setInviteSending(false);
  };

  const handleQuestionnaireChanged = (
    option: DropdownSelectOption | DropdownSelectOption[],
  ) => {
    const selectedOption = option as DropdownSelectOption;
    setSelectedQuestionnaireId(String(selectedOption.id));
    setQuestionnaireError(null);
  };

  const getParsedEmails = () =>
    Array.from(
      new Set(
        supplierEmails
          .split('\n')
          .map((email) => email.trim())
          .filter(Boolean)
          .map((email) => email.toLowerCase()),
      ),
    );

  const validateEmails = () => {
    const emails = getParsedEmails();

    if (emails.length === 0) {
      setEmailError('Please enter at least one supplier email.');
      return false;
    }

    const invalidEmail = emails.find((email) => !/^\S+@\S+\.\S+$/.test(email));

    if (invalidEmail) {
      setEmailError(`Invalid email: ${invalidEmail}`);
      return false;
    }

    setEmailError(null);
    return true;
  };

  const formatSupplierNameFromEmail = (email: string) => {
    const localPart = email.split('@')[0] ?? email;
    const formatted = localPart.replace(/[._-]+/g, ' ').trim();

    return formatted.length > 0 ? formatted : email;
  };

  const createInviteOnlySupplier = async (
    organisationId: string,
    email: string,
  ) => {
    if (!defaultInviteIndustryId) {
      throw new Error(
        'No default industry is available for creating invited suppliers.',
      );
    }

    const { data, error } = await supabase.rpc('create_supplier', {
      org_id: organisationId,
      supplier_name: formatSupplierNameFromEmail(email),
      industry_id: defaultInviteIndustryId,
      website: null,
      registration_number: null,
      p_street: null,
      p_city: null,
      p_state: null,
      p_postal_code: null,
      p_country: null,
      p_email: email,
    });

    if (error || !data) {
      throw new Error(
        error?.message ?? `Failed to create a supplier record for ${email}.`,
      );
    }

    const { error: linkError } = await supabase.rpc(
      'ensure_buyer_supplier_link',
      {
        p_buyer_org_id: organisationId,
        p_supplier_id: data,
      },
    );

    if (linkError) {
      throw new Error(linkError.message);
    }

    return String(data);
  };

  const ensureInvitableSuppliers = async (emails: string[]) => {
    const organisationId = selectedOrganisation?.organisation?.id;

    if (!organisationId) {
      throw new Error('Please select an organisation before sending invites.');
    }

    const { data: existingSuppliers, error: existingSuppliersError } =
      await supabase
        .from('suppliers')
        .select(
          `
            id,
            email,
            buyer_supplier_link!inner(supplier_id)
          `,
        )
        .in('email', emails)
        .eq('buyer_supplier_link.buyer_org_id', organisationId);

    if (existingSuppliersError) {
      throw new Error(existingSuppliersError.message);
    }

    const supplierIdsByEmail = new Map<string, string>();

    (existingSuppliers ?? []).forEach((supplier) => {
      if (supplier.email && supplier.id) {
        supplierIdsByEmail.set(supplier.email.toLowerCase(), supplier.id);
      }
    });

    const unresolvedEmails = emails.filter(
      (email) => !supplierIdsByEmail.has(email),
    );

    if (unresolvedEmails.length > 0) {
      const { data: reusableSuppliers, error: reusableSuppliersError } =
        await supabase
          .from('suppliers')
          .select('id, email')
          .in('email', unresolvedEmails);

      if (reusableSuppliersError) {
        throw new Error(reusableSuppliersError.message);
      }

      for (const supplier of reusableSuppliers ?? []) {
        if (!supplier.email || !supplier.id) {
          continue;
        }

        const normalizedEmail = supplier.email.toLowerCase();

        if (supplierIdsByEmail.has(normalizedEmail)) {
          continue;
        }

        const { error: linkError } = await supabase.rpc(
          'ensure_buyer_supplier_link',
          {
            p_buyer_org_id: organisationId,
            p_supplier_id: supplier.id,
          },
        );

        if (linkError) {
          throw new Error(linkError.message);
        }

        supplierIdsByEmail.set(normalizedEmail, supplier.id);
      }
    }

    for (const email of emails) {
      if (supplierIdsByEmail.has(email)) {
        continue;
      }

      const createdSupplierId = await createInviteOnlySupplier(
        organisationId,
        email,
      );

      supplierIdsByEmail.set(email, createdSupplierId);
    }

    return emails.map((email) => supplierIdsByEmail.get(email)).filter(Boolean);
  };

  const handleSendInvites = async () => {
    if (!validateEmails()) {
      setInviteStep(1);
      return;
    }

    if (questionnaireMode === 'standard' && !defaultOnboardingQuestionnaireId) {
      setQuestionnaireError(
        'The default onboarding questionnaire is not available right now.',
      );
      return;
    }

    if (questionnaireMode === 'custom' && !selectedQuestionnaireId) {
      setQuestionnaireError('Please select an onboarding questionnaire.');
      return;
    }

    setQuestionnaireError(null);
    setInviteError(null);
    setInviteSending(true);

    const organisationId = selectedOrganisation?.organisation?.id;
    const organisationName = selectedOrganisation?.organisation?.name ?? null;
    const emails = getParsedEmails();

    try {
      const supplierIds = await ensureInvitableSuppliers(emails);

      if (questionnaireMode === 'none') {
        const { error } = await supabase.from('supplier_invites').insert(
          emails.map((email) => ({
            email,
            assessment_id: null,
            template_title: null,
            inviting_organisation_name: organisationName,
            status: 'pending',
          })),
        );

        if (error) {
          throw new Error(error.message);
        }
      } else {
        const templateId =
          questionnaireMode === 'custom'
            ? selectedQuestionnaireId
            : defaultOnboardingQuestionnaireId;

        const { data, error } = await supabase.functions.invoke(
          'create-assessments',
          {
            body: {
              template_id: templateId,
              supplier_ids: supplierIds,
              organisation_id: organisationId,
            },
          },
        );

        if (error) {
          throw new Error(error.message);
        }

        const parsedResult = typeof data === 'string' ? JSON.parse(data) : data;

        if (parsedResult?.success === false) {
          throw new Error(
            parsedResult?.message ?? 'Failed to create supplier invites.',
          );
        }
      }

      handleCreated();
      setToastMessage(
        emails.length === 1
          ? 'Supplier invitation sent successfully'
          : 'Supplier invitations sent successfully',
      );
      setShowToast(true);
      resetInviteModal();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to send invites.';
      setInviteError(message);
    } finally {
      setInviteSending(false);
    }
  };

  const handleNextInviteStep = () => {
    if (!validateEmails()) {
      return;
    }

    setInviteStep(2);
  };

  return (
    <div>
      <style>
        {`
          .invite-suppliers-choice {
            margin-bottom: 0;
          }

          .invite-suppliers-choice .form-check-input {
            width: 1.2rem;
            height: 1.2rem;
            margin-top: 3px;
            border-radius: 999px;
            border: 1px solid #b8c2cc;
            background-color: #f8fafc;
            box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.06);
            transition:
              background-color 0.15s ease,
              border-color 0.15s ease,
              box-shadow 0.15s ease;
          }

          .invite-suppliers-choice .form-check-input:hover {
            border-color: #8b7ca1;
            background-color: #f3eef8;
          }

          .invite-suppliers-choice .form-check-input:focus {
            border-color: #2a245a;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.18);
          }

          .invite-suppliers-choice .form-check-input:checked {
            background-color: #2a245a;
            border-color: #2a245a;
          }

          .invite-suppliers-choice-card {
            border: 1px solid #d8e0e8;
            border-radius: 0.75rem;
            padding: 0.9rem 1rem;
            background: #fff;
            transition:
              border-color 0.15s ease,
              box-shadow 0.15s ease,
              background-color 0.15s ease;
          }

          .invite-suppliers-choice-card:hover {
            border-color: #b8c2cc;
            background-color: #fcfbfe;
          }

          .invite-suppliers-choice-card.is-active {
            border-color: #2a245a;
            box-shadow: 0 0 0 0.2rem rgba(89, 13, 253, 0.12);
            background-color: #f9f8ff;
          }

          .invite-suppliers-choice-card .form-check-label {
            width: 100%;
            font-weight: 500;
          }

          .invite-suppliers-choice-note {
            display: block;
            margin-top: 0.2rem;
            font-size: 0.875rem;
            color: #716c7d;
          }
        `}
      </style>
      {selectedOrganisation && (
        <h2 className="mb-3">
          Suppliers for {selectedOrganisation?.organisation?.name}
        </h2>
      )}
      <div className="mb-3">
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Add Supplier
        </Button>
        <Button
          variant="outline-primary"
          className="ms-2"
          onClick={() => {
            // navigate to bulk-import-suppliers page using router
            navigate('bulk-import-suppliers');
          }}
        >
          Import Suppliers
        </Button>
        <Button
          variant="outline-primary"
          className="ms-2"
          onClick={() => setShowInviteModal(true)}
        >
          Invite suppliers
        </Button>
      </div>

      <SupplierList reloadKey={reloadKey} />

      <CreateSupplierModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        onFinished={handleFinished}
      />

      <Modal show={showInviteModal} onHide={resetInviteModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Invite suppliers
            <span className="text-muted fs-6 ms-2">Step {inviteStep} of 2</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inviteError && (
            <Alert variant="danger" className="mb-3">
              {inviteError}
            </Alert>
          )}
          {inviteStep === 1 ? (
            <Form.Group className="mb-0" controlId="supplierInviteEmails">
              <p>
                Enter the email addresses of the suppliers you want to invite to
                join your SupplyPassport network. They will receive an email
                with instructions on how to create their account and connect
                with your organisation.
              </p>
              <Form.Label className="mb-0">Supplier emails</Form.Label>
              <Form.Text className="d-block text-muted mb-2 mt-0">
                one email per line
              </Form.Text>
              <Form.Control
                as="textarea"
                rows={8}
                value={supplierEmails}
                onChange={(e) => {
                  setSupplierEmails(e.target.value);
                  setEmailError(null);
                }}
                isInvalid={!!emailError}
                placeholder="supplier@example.com"
              />
              <Form.Control.Feedback type="invalid">
                {emailError}
              </Form.Control.Feedback>
            </Form.Group>
          ) : (
            <>
              <Form.Group className="mb-4">
                <Form.Label className="mb-2">
                  Onboarding questionnaire
                </Form.Label>

                <div className="d-grid gap-2">
                  <div
                    className={`invite-suppliers-choice-card ${
                      questionnaireMode === 'standard' ? 'is-active' : ''
                    }`}
                  >
                    <Form.Check
                      type="radio"
                      id="invite-questionnaire-standard"
                      name="inviteQuestionnaireMode"
                      className="invite-suppliers-choice"
                      checked={questionnaireMode === 'standard'}
                      onChange={() => {
                        setQuestionnaireMode('standard');
                        setQuestionnaireError(null);
                      }}
                      label={
                        <span>
                          Use standard SupplyPassport onboarding questionnaire
                          <span className="invite-suppliers-choice-note">
                            Attach the standard SupplyPassport onboarding
                            questionnaire
                            {defaultOnboardingQuestionnaireId && (
                              <>
                                {' '}
                                <a
                                  href={`/questionnaire/${defaultOnboardingQuestionnaireId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Preview questionnaire
                                </a>
                              </>
                            )}
                          </span>
                        </span>
                      }
                    />
                  </div>

                  <div
                    className={`invite-suppliers-choice-card ${
                      questionnaireMode === 'none' ? 'is-active' : ''
                    }`}
                  >
                    <Form.Check
                      type="radio"
                      id="invite-questionnaire-none"
                      name="inviteQuestionnaireMode"
                      className="invite-suppliers-choice"
                      checked={questionnaireMode === 'none'}
                      onChange={() => {
                        setQuestionnaireMode('none');
                        setQuestionnaireError(null);
                      }}
                      label={
                        <span>
                          No questionnaire
                          <span className="invite-suppliers-choice-note">
                            Send the invitation without attaching an onboarding
                            questionnaire.
                          </span>
                        </span>
                      }
                    />
                  </div>

                  <div
                    className={`invite-suppliers-choice-card ${
                      questionnaireMode === 'custom' ? 'is-active' : ''
                    }`}
                  >
                    <Form.Check
                      type="radio"
                      id="invite-questionnaire-custom"
                      name="inviteQuestionnaireMode"
                      className="invite-suppliers-choice"
                      checked={questionnaireMode === 'custom'}
                      onChange={() => {
                        setQuestionnaireMode('custom');
                        setQuestionnaireError(null);
                      }}
                      label={
                        <span>
                          Select questionnaire from the list
                          <span className="invite-suppliers-choice-note">
                            Choose one of your existing questionnaires to attach
                            to the invitation. Suppliers will be required to
                            complete the questionnaire during onboarding.
                          </span>
                        </span>
                      }
                    />
                  </div>
                </div>
              </Form.Group>

              {questionnaireMode === 'custom' && (
                <Form.Group controlId="supplierInviteQuestionnaire">
                  <Form.Label>Select onboarding questionnaire:</Form.Label>
                  <div className="mt-2">
                    <DropdownSelect
                      options={questionnaireOptions}
                      selectedIds={selectedQuestionnaireId}
                      onValueChanged={handleQuestionnaireChanged}
                      placeholder={
                        loadingQuestionnaires
                          ? 'Loading questionnaires...'
                          : 'Select...'
                      }
                      disabled={loadingQuestionnaires}
                      full
                      isInvalid={!!questionnaireError}
                    />
                  </div>
                  {questionnairesError && (
                    <div className="text-danger small mt-2">
                      Unable to load questionnaires.
                    </div>
                  )}
                  {questionnaireError && (
                    <div className="text-danger small mt-2">
                      {questionnaireError}
                    </div>
                  )}
                  {loadingQuestionnaires && (
                    <div className="text-muted small mt-2 d-flex align-items-center gap-2">
                      <Spinner animation="border" size="sm" />
                      Loading questionnaires...
                    </div>
                  )}
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div>
              {inviteStep === 2 && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setInviteStep(1)}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={resetInviteModal}>
                Cancel
              </Button>
              {inviteStep === 1 ? (
                <Button
                  variant="primary"
                  onClick={handleNextInviteStep}
                  disabled={inviteSending}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSendInvites}
                  disabled={inviteSending}
                >
                  {inviteSending ? 'Sending...' : 'Send'}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Toast notification */}
      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg={gToastSentiment}
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
